import Image from 'next/image';
import Link from 'next/link';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { ContentSectionDTO } from '@lib/strapi/dtos/components/sections/contentSection.dto';
import { Prose, openAppointmentModal } from '../shared-ui';
import { IconHome, IconPhone } from '@components/utils/icons';
import * as gtag from '@lib/utilities/analytics';

/**
 * Prose + optional image. The image takes the organic mask like everything
 * else; `Highlight` puts the block on the violet tint (the "join the team"
 * banner on About, per D16).
 *
 * Heading icons are SCOPED BY HEADING TEXT, not automatic: content-section is
 * the generic prose block (career page, treatment plan, …) and a blanket icon
 * would decorate headings it has nothing to say about. The map covers the
 * homepage intro (owner ask, 2026-09-06); an editor rewording the heading
 * simply loses the icon — nothing breaks.
 */
const HEADING_ICONS: Record<string, typeof IconHome> = {
  'Στο Physiowave': IconHome,
  'At Physiowave': IconHome,
  'Στο Physiowave Therapy': IconHome,
  'At Physiowave Therapy': IconHome,
};

const ContentSectionImplementation = ({ section, index }: ComponentProps<ContentSectionDTO>) => {
  if (!section) return null;
  const highlight = section.style === 'Highlight';
  const imageSide = section.style === 'ImageLeft' ? 'lg:order-first' : '';
  const HeadingIcon = section.heading ? HEADING_ICONS[section.heading.trim()] : undefined;

  return (
    <section className={`my-8 ${highlight ? 'rounded-2xl bg-violet-050 p-8' : ''}`}>
      <div className={`grid items-center gap-8 ${section.image ? 'lg:grid-cols-[1.2fr_0.8fr]' : ''}`}>
        <div>
          {section.heading ? (
            <h2 className="flex items-center gap-2.5 text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
              {HeadingIcon ? <span className="flex-none text-violet-700 [&>svg]:h-[0.85em] [&>svg]:w-[0.85em]" aria-hidden><HeadingIcon /></span> : null}
              {section.heading}
            </h2>
          ) : null}
          {section.subheading ? <p className="mt-2 text-lg text-muted">{section.subheading}</p> : null}
          <Prose content={section.content} className="mt-4" />
          {(section.links ?? []).length ? (
            <div className="mt-5 flex flex-wrap gap-3">
              {section.links.map((link, i) => {
                // a tel: link is THE conversion action — it takes the hero's
                // filled treatment (+ phone_click), everything else stays
                // the quiet outline pill
                const isTel = (link.url ?? '').startsWith('tel:');
                // booking CTAs open the popup (D47) so the visitor stays in
                // context; the /kleiste-rantevou PAGE is the direct-pointer
                // door for ads, shares and prose links (owner, 2026-09-08)
                const isBooking = (link.url ?? '').replace(/\/$/, '') === '/kleiste-rantevou';
                if (isBooking) {
                  return (
                    <button key={i} type="button" onClick={() => openAppointmentModal('content-section')}
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
                      {link.title}
                    </button>
                  );
                }
                return isTel ? (
                  <a key={i} href={link.url}
                    onClick={() => gtag.event('phone_click', { event_category: 'conversion', event_label: 'content-section' })}
                    className="inline-flex items-center gap-2 rounded-full bg-violet-700 px-5 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-violet-800">
                    <IconPhone width={16} height={16} aria-hidden />
                    {link.title}
                  </a>
                ) : (
                  <Link key={i} href={link.url ?? '#'}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
                    {link.title}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
        {section.image ? (
          <div className={`blob-2 relative mx-auto aspect-[4/3] w-full max-w-sm overflow-hidden bg-violet-400 ${imageSide}`}>
            <Image src={section.image.url} alt={section.image.alternativeText ?? ''} fill
              sizes="(min-width:1024px) 24rem, 90vw"
              // sections in zone slots 0-1 sit above/at the fold (e.g. the
              // about page opener) — their photo is the page's LCP candidate,
              // so it preloads (priority) and wins the bandwidth race
              // (fetchPriority). Deeper slots stay lazy (owner, 2026-09-16).
              priority={(index ?? 99) <= 1}
              fetchPriority={(index ?? 99) <= 1 ? 'high' : undefined} className="clinical-photo" />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ContentSectionImplementation;
